// @ts-check
// Página de registro (doc 10, seções 1 a 4; doc 07, 8.1): breadcrumb com título editável,
// coluna esquerda com cartão resumo e campos editáveis, abas Timeline / Tarefas / Notas.
import { html } from 'htm/preact';
import { Fragment } from 'preact';
import { useEffect, useMemo, useRef, useState } from 'preact/hooks';
import { Link, locationState, navigate } from '../../app/router.js';
import { objectsByNameSingular } from '../../data/metadata-store.js';
import { createLiveRecordList, deleteRecordOptimistically } from '../../data/record-store.js';
import { remoteRecordChangeState } from '../../data/live-updates.js';
import { findOneRecord, restoreOneRecord } from '../../data/rest.js';
import { canDestroyObjectRecords, canSoftDeleteObjectRecords } from '../../lib/permissions.js';
import { TrashConfirmModal, runTrashAction } from '../object-record/trash-actions.js';
import {
  getLabelIdentifierField,
  getRecordLabel,
  isFieldDisplayable,
  isOneToManyRelation,
} from '../../lib/field-metadata.js';
import { formatDateTime, formatRelativeToNow } from '../../lib/format.js';
import { PageCard, PageHeader } from '../page/page-card.js';
import { NotFoundPage } from '../../app/not-found-page.js';
import { hasStageScope } from '../../data/stage-scope.js';
import { Icon } from '../../ui/icon.js';
import { Button, IconButton } from '../../ui/button.js';
import { EmptyState, Skeleton } from '../../ui/feedback.js';
import { TabList } from '../../ui/tab-list.js';
import { Dropdown, DropdownMenuItems, MenuItem } from '../../ui/dropdown.js';
import { InlineCell, RecordTitleCell } from './inline-cell.js';
import { TimelineTab } from './timeline-tab.js';
import { TasksTab } from './tasks-tab.js';
import { NotesTab } from './notes-tab.js';
import { RecordConversationsTab } from '../conversations/conversations-page.js';
import { CALENDAR_TAB_FILTERS, RecordCalendarTab } from '../calendar/record-calendar-tab.js';
import { RelatedRecordsSection } from './related-records-section.js';
import { FilesTab, getAttachmentTargetField } from './files-tab.js';
import { getActivityBodyField } from './activity-targets.js';
import { DuplicatesSection } from './duplicates-section.js';
import { ActivityTargetsCell } from './activity-targets-cell.js';
import { ActivityBodyTab } from './activity-body-tab.js';
import { RecordImage } from './record-image.js';
import { RecordNavigationControls } from './record-navigation-controls.js';
import { favoritesState } from '../../data/metadata-store.js';
import { addFavorite, removeFavorite } from '../../data/parity.js';
import { isFieldDisplayedAsTable } from '../../lib/related-records.js';
import { findOption, getSortedOptions } from '../../lib/field-metadata.js';
import { updateRecordOptimistically } from '../../data/record-store.js';
import { canClassifyAsNotLead, classifyRecordAsNotLead, isNotLead } from '../object-record/not-lead.js';
import { showErrorSnackbar, showSuccessSnackbar } from '../../ui/snackbar.js';
import { t, translateMetadataLabel } from '../../i18n/index.js';
import { usePublishActiveRecord } from '../command-menu/use-active-record.js';
import { familyPostcodeHintSlot, withPostcodeRows } from '../../lib/address-sub-fields.js';
import { FamilyPostcodeHint } from './family-postcode-hint.js';
import { clearRecordHandedOff, readHandedOffStage, resolveHandedOffBackPath, resolveRecordShowStatus } from '../../lib/handed-off-records.js';
import { computeRecordNavigation } from '../../lib/record-navigation.js';
import { openRecord, recordNavigationState } from '../side-panel/open-record.js';
// lugares da 2.0.0 na ficha (spec-2.0.0 seção 5.3): LGPD não consentida (1A) e fluxos em que o card está (2D)
import { MarketingConsentRow } from '../privacy/marketing-consent-row.js';
import { RecordFlowsSection } from '../flows/record-flows-section.js';
// seções do módulo Atendimento (3.0.0): somem fora do próprio objeto e com o módulo desligado
import { ServiceTicketRecordSection } from '../service-desk/service-ticket-record-section.js';
import { HouseholdRetentionSection } from '../retention/household-retention-section.js';

const ACTIVITY_OBJECT_NAMES = new Set(['task', 'note']);
/** Objetos com a aba Conversas (contrato de conversas, seção 6); na família, as conversas das pessoas dela. */
const CONVERSATION_OBJECT_NAMES = new Set(['person', 'enrollmentOpportunity', 'household']);

/**
 * Campos do cartão: todos os exibíveis exceto o identificador, as relações com objetos de
 * sistema (taskTargets, noteTargets, timelineActivities) e o searchVector.
 * @param {import('../../lib/field-metadata.js').ObjectMetadata} objectMetadata
 */
function getRecordPageFields(objectMetadata) {
  const labelField = getLabelIdentifierField(objectMetadata);
  // o corpo da nota e da tarefa tem aba própria, com o editor de texto formatado
  const bodyField = ACTIVITY_OBJECT_NAMES.has(objectMetadata.nameSingular) ? getActivityBodyField(objectMetadata) : undefined;
  return objectMetadata.fields
    .filter((field) => isFieldDisplayable(field) && field.id !== labelField?.id && field.id !== bodyField?.id && !isFieldDisplayedAsTable(field))
    .filter((field) => {
      if (!isOneToManyRelation(field)) return true;
      const target = objectsByNameSingular.value.get(field.relation?.targetObjectNameSingular ?? '');
      return Boolean(target && !target.isSystem);
    })
    .sort((first, second) => {
      const firstIsSystem = first.isSystem || ['createdAt', 'updatedAt', 'createdBy', 'updatedBy'].includes(first.name) ? 1 : 0;
      const secondIsSystem = second.isSystem || ['createdAt', 'updatedAt', 'createdBy', 'updatedBy'].includes(second.name) ? 1 : 0;
      if (firstIsSystem !== secondIsSystem) return firstIsSystem - secondIsSystem;
      return (first.position ?? 0) - (second.position ?? 0);
    });
}

/**
 * Excluir definitivamente o registro da lixeira (doc 10, seção 9.5): confirmação e volta para a lista do objeto.
 * @param {{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, recordId: string, onClose: () => void }} props
 */
function DestroyRecordModal({ objectMetadata, recordId, onClose }) {
  const [isWorking, setIsWorking] = useState(false);
  const confirm = async () => {
    setIsWorking(true);
    try {
      const doneIds = await runTrashAction(objectMetadata, 'destroy', [recordId]);
      if (doneIds.includes(recordId)) navigate(`/objects/${objectMetadata.namePlural}`);
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsWorking(false);
      onClose();
    }
  };
  return html`<${TrashConfirmModal} action="destroy" count=${1} isLoading=${isWorking} onClose=${onClose} onConfirm=${confirm} />`;
}

/**
 * @param {{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, record: any, onRestore?: () => void }} props
 */
function RecordActionsMenu({ objectMetadata, record, onRestore }) {
  const anchorRef = useRef(/** @type {HTMLSpanElement | null} */ (null));
  const [isOpen, setIsOpen] = useState(false);
  const [isConfirmingDestroy, setIsConfirmingDestroy] = useState(false);
  const favorite = favoritesState.value.find((item) => item.type === 'RECORD' && item.targetRecordId === record.id);
  return html`<span ref=${anchorRef}>
    ${record.deletedAt
      ? null
      : html`<${IconButton}
          icon=${favorite ? 'IconHeartOff' : 'IconHeart'}
          size="small"
          variant="tertiary"
          ariaLabel=${favorite ? t('parity.favorites.remove') : t('parity.favorites.add')}
          data-testid="record-favorite"
          onClick=${() => (favorite ? removeFavorite(favorite.id) : addFavorite({ type: 'RECORD', objectNameSingular: objectMetadata.nameSingular, recordId: record.id }))
            .catch(showErrorSnackbar)}
        />`}
    <${IconButton} icon="IconDotsVertical" size="small" ariaLabel=${t('recordShow.actions')} onClick=${() => setIsOpen(!isOpen)} />
    <${Dropdown} anchorRef=${anchorRef} isOpen=${isOpen} onClose=${() => setIsOpen(false)} placement="bottom-end" width=${220}>
      ${record.deletedAt
        ? html`<${DropdownMenuItems}>
            ${canSoftDeleteObjectRecords(objectMetadata) && onRestore
              ? html`<${MenuItem} icon="IconRestore" text=${t('trash.restore')} onClick=${() => {
                  setIsOpen(false);
                  onRestore();
                }} />`
              : null}
            ${canDestroyObjectRecords(objectMetadata)
              ? html`<${MenuItem} icon="IconTrashX" accent="danger" text=${t('trash.destroy')} onClick=${() => {
                  setIsOpen(false);
                  setIsConfirmingDestroy(true);
                }} />`
              : null}
          <//>`
        : html`<${DropdownMenuItems}>
        ${canClassifyAsNotLead(objectMetadata, record)
          ? html`<${MenuItem}
              icon="IconUserOff"
              text=${t('notLead.action')}
              onClick=${() => {
                setIsOpen(false);
                classifyRecordAsNotLead(objectMetadata, record);
              }}
            />`
          : null}
        <${MenuItem}
          icon="IconTrash"
          accent="danger"
          text=${t('recordShow.delete', { object: translateMetadataLabel(objectMetadata.labelSingular) })}
          onClick=${async () => {
            setIsOpen(false);
            try {
              await deleteRecordOptimistically(objectMetadata, record.id);
              showSuccessSnackbar(t('recordShow.deleted'));
              navigate(`/objects/${objectMetadata.namePlural}`);
            } catch (error) {
              showErrorSnackbar(error);
            }
          }}
        />
      <//>`}
    <//>
    ${isConfirmingDestroy
      ? html`<${DestroyRecordModal} objectMetadata=${objectMetadata} recordId=${record.id} onClose=${() => setIsConfirmingDestroy(false)} />`
      : null}
  </span>`;
}

/**
 * Card classificado como "não é lead": motivo e volta ao funil (primeira etapa), que limpa o motivo no motor.
 * @param {{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, record: any }} props
 */
function NotLeadBanner({ objectMetadata, record }) {
  const [isSaving, setIsSaving] = useState(false);
  const reasonField = objectMetadata.fields.find((field) => field.name === 'notLeadReason');
  const stageField = objectMetadata.fields.find((field) => field.name === 'stage');
  const reason = reasonField ? findOption(reasonField, record.notLeadReason) : null;
  const reasonText = [reason ? translateMetadataLabel(reason.label) : '', record.notLeadReasonDetail ?? ''].filter(Boolean).join(' · ');
  const firstStage = stageField ? getSortedOptions(stageField)[0]?.value : null;
  const backToFunnel = async () => {
    if (!firstStage) return;
    setIsSaving(true);
    try {
      await updateRecordOptimistically(objectMetadata, record.id, { stage: firstStage }, { stage: firstStage, notLeadReason: null, notLeadReasonDetail: null });
      showSuccessSnackbar(t('notLead.backedToFunnel'));
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsSaving(false);
    }
  };
  return html`<div class="information-banner" data-variant="neutral" data-testid="not-lead-banner">
    <span>${t('notLead.banner', { reason: reasonText || '-' })}</span>
    <span class="information-banner-hint">${t('notLead.bannerHint')}</span>
    ${firstStage ? html`<${Button} title=${t('notLead.backToFunnel')} icon="IconArrowBackUp" variant="secondary" size="small" isLoading=${isSaving} onClick=${backToFunnel} />` : null}
  </div>`;
}

/**
 * Registro aberto na página ou no painel: carrega com a lixeira incluída (banner de excluído) e recarrega depois de
 * mudanças em filhos, que recalculam contagens e valores do pai.
 * @param {string} objectNameSingular @param {string} recordId
 */
export function useRecordShowData(objectNameSingular, recordId) {
  const objectMetadata = objectsByNameSingular.value.get(objectNameSingular);
  // o registro aberto continua na tela mesmo excluído por outra pessoa: a releitura mostra o aviso de excluído
  const liveList = useMemo(() => createLiveRecordList(objectNameSingular, [], { isTrash: true }), [objectNameSingular, recordId]);
  useEffect(() => () => liveList.dispose(), [liveList]);
  const [status, setStatus] = useState(/** @type {'loading' | 'ready' | 'notFound'} */ ('loading'));
  const record = liveList.records.value[0];

  const reloadRecord = () => {
    if (!objectMetadata) return;
    findOneRecord(objectMetadata, recordId, { depth: 1, withDeleted: true })
      .then((loadedRecord) => {
        if (loadedRecord) liveList.records.value = [loadedRecord];
      })
      .catch((error) => {
        // o card foi para uma etapa que o papel não vê (ou deixou de existir): a tela limpa o registro
        if (error?.statusCode === 404) {
          liveList.records.value = [];
          setStatus('notFound');
          return;
        }
        showErrorSnackbar(error);
      });
  };

  useEffect(() => {
    if (!objectMetadata) return;
    setStatus('loading');
    findOneRecord(objectMetadata, recordId, { depth: 1, withDeleted: true })
      .then((loadedRecord) => {
        if (!loadedRecord) {
          setStatus('notFound');
          return;
        }
        // lido de novo: outra pessoa trouxe o card de volta para uma etapa que o papel vê
        clearRecordHandedOff(objectNameSingular, recordId);
        liveList.records.value = [loadedRecord];
        setStatus('ready');
      })
      .catch((error) => {
        if (error?.statusCode !== 404) showErrorSnackbar(error);
        setStatus('notFound');
      });
  }, [objectMetadata?.id, recordId, liveList]);

  // outra pessoa mudou este registro (feed de mudanças, contrato 08 de navegação): relê sem esqueleto
  const remoteChange = remoteRecordChangeState.value;
  useEffect(() => {
    if (remoteChange.sequence === 0 || status !== 'ready') return;
    if (remoteChange.isReset || remoteChange.keys.has(`${objectNameSingular}:${recordId}`)) reloadRecord();
  }, [remoteChange.sequence]);

  // o próprio membro passou o card para uma etapa de Pode passar: o record-store tirou o registro da lista, e a tela
  // mostra o repasse (também ao voltar para o endereço do card), não o 404
  const handedOffStage = readHandedOffStage(objectNameSingular, recordId);
  const effectiveStatus = resolveRecordShowStatus(status, Boolean(record), handedOffStage);
  return { objectMetadata, record, status: effectiveStatus, reloadRecord, handedOffStage };
}

/**
 * Abas do registro (doc 10, 5.1): notas e tarefas abrem no corpo, como as abas "Note" do Twenty.
 * @param {string} objectNameSingular
 */
export function getRecordTabs(objectNameSingular) {
  const isActivity = ACTIVITY_OBJECT_NAMES.has(objectNameSingular);
  return [
    ...(isActivity ? [{ id: 'body', title: t(objectNameSingular === 'task' ? 'recordPage.tabs.taskBody' : 'recordPage.tabs.noteBody'), icon: 'IconNotes' }] : []),
    { id: 'timeline', title: t('recordShow.tabs.timeline'), icon: 'IconTimelineEvent' },
    ...(isActivity ? [] : [
      { id: 'tasks', title: t('recordShow.tabs.tasks'), icon: 'IconCheckbox' },
      { id: 'notes', title: t('recordShow.tabs.notes'), icon: 'IconNotes' },
    ]),
    ...(getAttachmentTargetField(objectNameSingular) ? [{ id: 'files', title: t('parity.files.title'), icon: 'IconPaperclip' }] : []),
    ...(CONVERSATION_OBJECT_NAMES.has(objectNameSingular) ? [{ id: 'conversations', title: t('recordShow.tabs.conversations'), icon: 'IconMessages' }] : []),
    // agenda do card, da pessoa e da família (contrato 08 da agenda)
    ...(objectNameSingular in CALENDAR_TAB_FILTERS ? [{ id: 'calendar', title: t('calendar.recordTab'), icon: 'IconCalendar' }] : []),
  ];
}

/**
 * Cartão resumo (doc 10, seção 2): foto ou logo com envio e recorte, título editável e "Adicionado há".
 * @param {{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, record: any, isReadOnly: boolean, layout?: 'column' | 'row' }} props
 */
export function RecordSummaryCard({ objectMetadata, record, isReadOnly, layout = 'column' }) {
  const labelField = getLabelIdentifierField(objectMetadata);
  const recordLabel = getRecordLabel(objectMetadata, record);
  return html`<div class="record-summary-card" data-layout=${layout}>
    <${RecordImage} objectMetadata=${objectMetadata} record=${record} isReadOnly=${isReadOnly} />
    <div class="record-summary-texts">
      <${RecordTitleCell} objectMetadata=${objectMetadata} field=${labelField} record=${record} size="md" isReadOnly=${isReadOnly} />
      ${record.createdAt
        ? html`<span class="record-summary-date" title=${formatDateTime(record.createdAt)}>${t('recordShow.added', { relative: formatRelativeToNow(record.createdAt) })}</span>`
        : null}
    </div>
  </div>`;
}

/**
 * Campos do registro; notas e tarefas mostram as Relações (vínculos) primeiro.
 * @param {{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, record: any, isReadOnly: boolean }} props
 */
export function RecordFieldsList({ objectMetadata, record, isReadOnly }) {
  const isActivity = ACTIVITY_OBJECT_NAMES.has(objectMetadata.nameSingular);
  const fields = withPostcodeRows(objectMetadata, getRecordPageFields(objectMetadata));
  // CEP da família no card: logo depois da Etapa, na primeira tela, sem rolar até a linha Família
  const postcodeHint = familyPostcodeHintSlot(objectMetadata.nameSingular, fields.map((field) => field.name));
  const hint = postcodeHint.isShown ? html`<${FamilyPostcodeHint} record=${record} />` : null;
  return html`<div class="record-fields-card">
    ${isActivity
      ? html`<${ActivityTargetsCell} activityNameSingular=${/** @type {'task' | 'note'} */ (objectMetadata.nameSingular)} activityId=${record.id} isReadOnly=${isReadOnly} />`
      : null}
    ${postcodeHint.isShown && postcodeHint.afterFieldName === null ? hint : null}
    ${fields.map(
      (field) => html`<${Fragment} key=${field.id}>
        <${InlineCell} objectMetadata=${objectMetadata} field=${field} record=${record} isRecordReadOnly=${isReadOnly} />
        ${postcodeHint.afterFieldName === field.name ? hint : null}
      <//>`,
    )}
  </div>`;
}

/**
 * Conteúdo da aba escolhida, igual na página e no painel.
 * @param {{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, record: any, activeTab: string, isReadOnly: boolean }} props
 */
export function RecordTabContent({ objectMetadata, record, activeTab, isReadOnly }) {
  const objectNameSingular = objectMetadata.nameSingular;
  const recordId = record.id;
  switch (activeTab) {
    case 'body':
      return html`<${ActivityBodyTab} objectMetadata=${objectMetadata} record=${record} isReadOnly=${isReadOnly} />`;
    case 'calendar':
      return objectNameSingular in CALENDAR_TAB_FILTERS ? html`<${RecordCalendarTab} objectNameSingular=${objectNameSingular} record=${record} />` : null;
    case 'files':
      return html`<${FilesTab} objectNameSingular=${objectNameSingular} recordId=${recordId} isReadOnly=${isReadOnly} />`;
    case 'conversations':
      return html`<${RecordConversationsTab} objectNameSingular=${objectNameSingular} record=${record} />`;
    case 'tasks':
      return html`<${TasksTab} objectNameSingular=${objectNameSingular} recordId=${recordId} />`;
    case 'notes':
      return html`<${NotesTab} objectNameSingular=${objectNameSingular} recordId=${recordId} />`;
    default:
      return html`<${TimelineTab} objectMetadata=${objectMetadata} recordId=${recordId} recordLabel=${getRecordLabel(objectMetadata, record) || t('record.untitled')} />`;
  }
}

/**
 * @param {{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, record: any, isRestoring: boolean, onRestore: () => void, onDestroy?: () => void }} props
 */
export function DeletedRecordBanner({ objectMetadata, isRestoring, onRestore, onDestroy }) {
  return html`<div class="information-banner record-deleted-banner" data-variant="danger" data-testid="record-deleted-banner">
    <span>${t('recordShow.deletedBanner')}</span>
    ${canSoftDeleteObjectRecords(objectMetadata)
      ? html`<${Button} title=${t('recordShow.restore')} icon="IconRestore" size="small" variant="secondary" isLoading=${isRestoring} onClick=${onRestore} />`
      : null}
    ${onDestroy && canDestroyObjectRecords(objectMetadata)
      ? html`<${Button} title=${t('trash.destroy')} icon="IconTrashX" size="small" variant="secondary" onClick=${onDestroy} />`
      : null}
  </div>`;
}

/**
 * Restaurar da lixeira, com o estado do botão.
 * @param {import('../../lib/field-metadata.js').ObjectMetadata | undefined} objectMetadata @param {string} recordId @param {() => void} onRestored
 */
export function useRestoreRecord(objectMetadata, recordId, onRestored) {
  const [isRestoring, setIsRestoring] = useState(false);
  const restoreRecord = async () => {
    if (!objectMetadata) return;
    setIsRestoring(true);
    try {
      await restoreOneRecord(objectMetadata, recordId);
      showSuccessSnackbar(t('recordShow.restoredMessage'));
      onRestored();
    } catch (error) {
      showErrorSnackbar(error);
    } finally {
      setIsRestoring(false);
    }
  };
  return { isRestoring, restoreRecord };
}

/**
 * Card que o próprio membro acabou de passar (Pode passar): em vez do 404, diz para onde o card foi e leva de volta à
 * lista de onde ele foi aberto, ou ao próximo da navegação "n de total". No painel lateral, o botão fecha o painel
 * (a lista está logo atrás) ou volta ao registro anterior da pilha.
 * @param {{ objectMetadata: import('../../lib/field-metadata.js').ObjectMetadata, recordId: string, stageLabel: string,
 *   panelAction?: { label: string, onClick: () => void } | null }} props
 */
export function RecordHandedOffState({ objectMetadata, recordId, stageLabel, panelAction = null }) {
  const context = recordNavigationState.value;
  const navigation = panelAction ? null : computeRecordNavigation(context, objectMetadata.nameSingular, recordId);
  const nextId = navigation?.nextId && navigation.nextId !== recordId ? navigation.nextId : null;
  const backPath = resolveHandedOffBackPath(context, objectMetadata, recordId);
  return html`<div data-testid="record-handed-off">
    <${EmptyState}
      icon="IconLogout"
      title=${t('recordPage.handedOff.title', { stage: stageLabel })}
      subtitle=${t('recordPage.handedOff.subtitle')}
      action=${html`<div class="record-handed-off-actions">
        ${panelAction
          ? html`<${Button} variant="secondary" title=${panelAction.label} onClick=${panelAction.onClick} />`
          : html`<${Button} variant="secondary" icon="IconArrowLeft" title=${t('recordPage.handedOff.backToList', { object: translateMetadataLabel(objectMetadata.labelPlural) })}
              onClick=${() => navigate(backPath)} />`}
        ${nextId
          ? html`<${Button} variant="secondary" icon="IconChevronDown" title=${t('recordPage.handedOff.openNext')}
              onClick=${() => openRecord({ objectNameSingular: objectMetadata.nameSingular, recordId: nextId, openIn: 'RECORD_PAGE' })} />`
          : null}
      </div>`}
    />
  </div>`;
}

/**
 * @param {{ objectNameSingular: string, recordId: string }} props
 */
export function RecordShowPage({ objectNameSingular, recordId }) {
  const { objectMetadata, record, status, reloadRecord, handedOffStage } = useRecordShowData(objectNameSingular, recordId);
  const tabs = getRecordTabs(objectNameSingular);
  const hashTab = locationState.value.hash.replace('#', '');
  const [activeTab, setActiveTab] = useState(tabs.some((tab) => tab.id === hashTab) ? hashTab : tabs[0].id);
  const { isRestoring, restoreRecord } = useRestoreRecord(objectMetadata, recordId, reloadRecord);
  const [isConfirmingDestroy, setIsConfirmingDestroy] = useState(false);

  const recordLabel = objectMetadata && record ? getRecordLabel(objectMetadata, record) : '';
  useEffect(() => {
    if (objectMetadata) {
      const objectLabel = translateMetadataLabel(objectMetadata.labelSingular);
      document.title = recordLabel ? `${recordLabel} - ${objectLabel}` : objectLabel;
    }
  }, [recordLabel, objectMetadata?.id]);

  // ações do registro no menu de comandos e lista de recentes da busca
  usePublishActiveRecord({ objectMetadata, record, reload: reloadRecord, restore: restoreRecord });

  if (objectMetadata && status === 'handedOff') {
    return html`<${PageCard}>
      <div class="page-centered">
        <${RecordHandedOffState} objectMetadata=${objectMetadata} recordId=${recordId} stageLabel=${handedOffStage ?? ''} />
      </div>
    <//>`;
  }
  if (!objectMetadata || status === 'notFound') return html`<${NotFoundPage} subtitle=${hasStageScope() ? t('recordPage.outOfScope') : undefined} />`;

  const labelField = getLabelIdentifierField(objectMetadata);
  const isDeleted = Boolean(record?.deletedAt);
  const breadcrumb = html`<nav class="record-breadcrumb">
    <${Link} href=${`/objects/${objectMetadata.namePlural}`} className="record-breadcrumb-prefix">
      <${Icon} name=${objectMetadata.icon ?? 'IconCube'} size=${16} />
      <span>${translateMetadataLabel(objectMetadata.labelPlural)}</span>
    <//>
    <span class="record-breadcrumb-separator">/</span>
    ${record
      ? html`<${RecordTitleCell} objectMetadata=${objectMetadata} field=${labelField} record=${record} size="xs" isReadOnly=${isDeleted} />`
      : html`<${Skeleton} width=${120} height=${16} />`}
    <${RecordNavigationControls} objectMetadata=${objectMetadata} recordId=${recordId} />
  </nav>`;

  return html`<${PageCard}
    header=${html`<${PageHeader} breadcrumb=${breadcrumb} actions=${record ? html`<${RecordActionsMenu} objectMetadata=${objectMetadata} record=${record} onRestore=${restoreRecord} />` : null} />`}
  >
    ${isDeleted
      ? html`<${DeletedRecordBanner} objectMetadata=${objectMetadata} record=${record} isRestoring=${isRestoring} onRestore=${restoreRecord} onDestroy=${() => setIsConfirmingDestroy(true)} />`
      : null}
    ${isConfirmingDestroy ? html`<${DestroyRecordModal} objectMetadata=${objectMetadata} recordId=${recordId} onClose=${() => setIsConfirmingDestroy(false)} />` : null}
    ${record && !isDeleted && isNotLead(record) ? html`<${NotLeadBanner} objectMetadata=${objectMetadata} record=${record} />` : null}
    <div class="record-show">
      <aside class="record-show-left">
        ${record
          ? html`<${RecordSummaryCard} objectMetadata=${objectMetadata} record=${record} isReadOnly=${isDeleted} />`
          : html`<div class="record-summary-card"><${Skeleton} width=${40} height=${40} /><${Skeleton} width=${96} height=${16} /></div>`}
        ${record
          ? html`<${RecordFieldsList} objectMetadata=${objectMetadata} record=${record} isReadOnly=${isDeleted} />`
          : html`<div class="record-fields-card">${[0, 1, 2, 3, 4].map((index) => html`<div key=${index} class="inline-cell"><${Skeleton} width="100%" height=${24} /></div>`)}</div>`}
        ${record && !isDeleted ? html`<${MarketingConsentRow} objectMetadata=${objectMetadata} record=${record} onChanged=${reloadRecord} />` : null}
      </aside>
      <section class="record-show-right">
        ${record ? html`<${DuplicatesSection} objectMetadata=${objectMetadata} record=${record} onMerged=${reloadRecord} />` : null}
        ${record && !isDeleted ? html`<${RecordFlowsSection} objectMetadata=${objectMetadata} record=${record} onChanged=${reloadRecord} />` : null}
        ${record && !isDeleted ? html`<${ServiceTicketRecordSection} objectMetadata=${objectMetadata} record=${record} onChanged=${reloadRecord} />` : null}
        ${record && !isDeleted ? html`<${HouseholdRetentionSection} objectMetadata=${objectMetadata} record=${record} onChanged=${reloadRecord} />` : null}
        ${record
          ? objectMetadata.fields
              .filter((field) => field.isActive !== false && isFieldDisplayedAsTable(field))
              .map((field) => html`<${RelatedRecordsSection}
                key=${field.id}
                objectMetadata=${objectMetadata}
                field=${field}
                record=${record}
                isReadOnly=${isDeleted}
                onChildrenChanged=${reloadRecord}
              />`)
          : null}
        <${TabList}
          tabs=${tabs}
          activeTabId=${activeTab}
          onChange=${(tabId) => {
            setActiveTab(tabId);
            window.history.replaceState(null, '', `${window.location.pathname}${window.location.search}#${tabId}`);
          }}
        />
        <div class="record-show-tab-content" data-tab=${activeTab}>
          ${record ? html`<${RecordTabContent} objectMetadata=${objectMetadata} record=${record} activeTab=${activeTab} isReadOnly=${isDeleted} />` : null}
        </div>
      </section>
    </div>
  <//>`;
}
